import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft,
  ShieldCheck,
  ShieldAlert,
  Building,
  Mail,
  Calendar,
  AlertTriangle,
  Shield,
  AppWindow,
  Check,
  Search,
  X,
  User as UserIcon,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTenants } from '@/store/tenant';
import { useRoles } from '@/store/role';
import { useApplications } from '@/store/application';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import { Button } from '@/components/ui/Button';
import { http } from '@/libs/http';
import type { User, Role } from '@/types';
import { PageLayout } from '@/components/page-layout';

export const UserAccessApprovalPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useLayout();
  const { tenants, list: listTenants } = useTenants();
  const { roles, list: listRoles } = useRoles();
  const { applications, list: listApps } = useApplications();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [comment, setComment] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [roleSearch, setRoleSearch] = useState('');

  useEffect(() => {
    setBreadcrumbs([
      { name: 'Tasks', to: '/m/tasks' },
      { name: 'User Access Request' },
    ]);
    listTenants();
    listRoles();
    listApps();

    if (!id) return;
    setLoading(true);
    http
      .get<User>(`/mgmt/v1/users/${id}`)
      .then((data) => {
        setUser(data);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load user access request');
      })
      .finally(() => setLoading(false));
  }, [id, setBreadcrumbs]);

  const handleReview = async () => {
    if (!user || !actionType) return;
    if (!comment.trim()) {
      toast.error('Justification is required');
      return;
    }

    setSubmitting(true);
    try {
      await http.post(`/mgmt/v1/users/${user.id}/review`, {
        action: actionType,
        comment: comment.trim(),
        role_ids: actionType === 'approve' ? selectedRoleIds : [],
      });

      toast.success(
        `User ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`
      );
      navigate('/m/tasks');
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleRoleSelection = (roleId: string) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((rId) => rId !== roleId) : [...prev, roleId]
    );
  };

  const handleCancel = () => {
    setActionType(null);
    setComment('');
    setSelectedRoleIds([]);
    setRoleSearch('');
  };

  const tenant = user ? tenants.find((t) => t.id === user.tenant_id) : null;

  const getTenantName = (tenantId?: string) => {
    if (!tenantId) return 'Unknown Tenant';
    const t = tenants.find((item) => item.id === tenantId);
    return t ? t.name : 'Unknown Tenant';
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full flex justify-center items-center h-[50vh]">
        <Loader />
      </div>
    );
  }

  if (!user) {
    return (
      <PageLayout
        title="Task Not Found"
        description="The requested user access task could not be found or may have been processed."
      >
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-12 text-center border border-slate-100 dark:border-slate-800">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">
            Task Not Found
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 max-w-sm mx-auto">
            This task ID may no longer exist or you might not have permission to view it.
          </p>
          <Button
            onClick={() => navigate('/m/tasks')}
            className="mt-6 rounded-full px-6 bg-[#1e293b] hover:bg-slate-800 text-white"
          >
            Return to Tasks
          </Button>
        </div>
      </PageLayout>
    );
  }

  // Format role kind/scope string
  const getRoleKindLabel = (role: Role) => {
    if (role.resource_kind_id) return role.resource_kind_id;
    if (role.applicable_scopes && role.applicable_scopes.length > 0) return role.applicable_scopes[0];
    return 'platform::tenant';
  };

  // Group roles by Application vs Tenant (outside application grouping)
  const appRoleGroups: { [appKey: string]: { appName: string; logoUrl?: string; roles: Role[] } } = {};
  const tenantRolesList: Role[] = [];

  roles.forEach((role) => {
    const kind = (role.resource_kind_id || '').toLowerCase();
    const idLower = role.id.toLowerCase();
    const isApp = kind.includes('application') || kind.includes('app') || idLower.startsWith('platform::application');

    if (isApp) {
      const matchingApp = applications.find(
        (app) =>
          kind.includes(app.name.toLowerCase()) ||
          kind.includes(app.id.toLowerCase()) ||
          idLower.includes(app.name.toLowerCase()) ||
          idLower.includes(app.id.toLowerCase())
      );

      const appKey = matchingApp ? matchingApp.id : (role.resource_kind_id || 'Application');
      const appName = matchingApp ? matchingApp.name : (role.resource_kind_id ? role.resource_kind_id.replace('platform::application::', '').replace('platform::', '') : 'Application Scope');
      const logoUrl = matchingApp?.logo_url;

      if (!appRoleGroups[appKey]) {
        appRoleGroups[appKey] = { appName, logoUrl, roles: [] };
      }
      appRoleGroups[appKey].roles.push(role);
    } else {
      tenantRolesList.push(role);
    }
  });

  const availableRolesCount = roles.length;

  // Flatten all roles for search
  const allRoleGroups: { groupLabel: string; groupIcon?: string; isApp: boolean; logoUrl?: string; roles: Role[] }[] = [];
  if (tenantRolesList.length > 0) {
    allRoleGroups.push({ groupLabel: 'Tenant & Platform Roles', isApp: false, logoUrl: tenant?.logo_url, roles: tenantRolesList });
  }
  Object.values(appRoleGroups).forEach((g) => {
    allRoleGroups.push({ groupLabel: g.appName, isApp: true, logoUrl: g.logoUrl, roles: g.roles });
  });

  const filteredGroups = allRoleGroups.map((group) => ({
    ...group,
    roles: group.roles.filter(
      (r) =>
        roleSearch.trim() === '' ||
        r.name.toLowerCase().includes(roleSearch.toLowerCase()) ||
        (r.description || '').toLowerCase().includes(roleSearch.toLowerCase())
    ),
  })).filter((g) => g.roles.length > 0);

  const selectedRoles = roles.filter((r) => selectedRoleIds.includes(r.id));

  const userInitial = user.name ? user.name[0].toUpperCase() : user.email[0].toUpperCase();

  const modeAccent = actionType === 'approve'
    ? 'border-emerald-500/40 dark:border-emerald-500/30'
    : actionType === 'reject'
      ? 'border-red-500/40 dark:border-red-500/30'
      : 'border-slate-200 dark:border-slate-800';

  const modeHeaderBg = actionType === 'approve'
    ? 'bg-emerald-500/5 dark:bg-emerald-500/5 border-b border-emerald-500/20 dark:border-emerald-500/10'
    : actionType === 'reject'
      ? 'bg-red-500/5 dark:bg-red-500/5 border-b border-red-500/20 dark:border-red-500/10'
      : 'bg-transparent border-b border-slate-100 dark:border-slate-800';

  return (
    <PageLayout
      title="User Access Request"
      description="Review user details and decide whether to approve or reject access."
    >
      <div className={`bg-white dark:bg-slate-900 rounded-2xl border overflow-hidden shadow-xs transition-colors duration-300 ${modeAccent}`}>

        {/* Top Navigation Bar */}
        <div className={`px-6 py-4 flex items-center justify-between transition-colors duration-300 ${modeHeaderBg}`}>
          <Link
            to="/m/tasks"
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full text-slate-500 dark:text-slate-400 transition-colors flex items-center gap-1.5 text-sm font-bold"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back to Tasks</span>
          </Link>
          <div className="flex items-center gap-3">
            {actionType && (
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${actionType === 'approve'
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                : 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
                }`}>
                {actionType === 'approve' ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                {actionType === 'approve' ? 'Approving Access' : 'Rejecting Access'}
              </span>
            )}
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 uppercase tracking-wider flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" />
              Access Request
            </span>
          </div>
        </div>

        <div className="p-6 lg:p-8 space-y-6">

          {/* ── User Identity Hero Card ── */}
          <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">

            <div className="p-6 flex flex-col sm:flex-row sm:items-center gap-6">
              {/* Avatar */}
              <div className="relative shrink-0">
                <div className="w-14 h-14 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold text-2xl border border-amber-500/20">
                  {userInitial}
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-amber-500 border-2 border-white dark:border-slate-900 flex items-center justify-center">
                  <Clock className="w-2.5 h-2.5 text-white" />
                </div>
              </div>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white truncate">
                    {user.name || 'No Display Name'}
                  </h2>
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 shrink-0">
                    Pending Review
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <Mail className="w-3.5 h-3.5 text-slate-400" />
                    </div>
                    <span className="truncate">{user.email}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0 overflow-hidden">
                      {tenant?.logo_url ? (
                        <img src={tenant.logo_url} alt={tenant.name} className="w-full h-full object-cover" />
                      ) : (
                        <Building className="w-3.5 h-3.5 text-slate-400" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <span className="font-semibold text-slate-800 dark:text-slate-200 truncate block">
                        {tenant?.name || getTenantName(user.tenant_id)}
                      </span>
                      <span className="text-[10px] text-slate-400">Requested Tenant</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    </div>
                    <div className="min-w-0">
                      <span className="text-slate-700 dark:text-slate-300">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                      </span>
                      <span className="text-[10px] text-slate-400 block">Date Requested</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Action Footer (shown only when no decision made yet) ── */}
            {actionType === null && (
              <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-center gap-3 animate-in fade-in duration-200">
                <button
                  id="approve-decision-btn"
                  onClick={() => setActionType('approve')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors cursor-pointer"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Approve Access
                </button>
                <button
                  id="reject-decision-btn"
                  onClick={() => setActionType('reject')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-red-50 dark:hover:bg-red-950/20 hover:border-red-300 dark:hover:border-red-800 text-red-600 dark:text-red-400 text-sm font-semibold transition-colors cursor-pointer"
                >
                  <ShieldAlert className="w-4 h-4" />
                  Reject Request
                </button>
              </div>
            )}
          </div>

          {/* ── Action Review Panel (shown after decision) ── */}
          {actionType !== null && (
            <div className="space-y-5 animate-in slide-in-from-bottom-3 duration-300">

              {/* ── Role Assignment (Approve Only) ── */}
              {actionType === 'approve' && (
                <div className="rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
                  {/* Section Header */}
                  <div className="px-5 py-3.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-orange-500 shrink-0" />
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white">Role Assignment</h4>
                      <span className="text-[10px] text-slate-400 font-normal">{availableRolesCount} available</span>
                    </div>
                    {selectedRoleIds.length > 0 && (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 shrink-0">
                        {selectedRoleIds.length} selected
                      </span>
                    )}
                  </div>

                  <div className="p-5 space-y-4">
                    {/* Warning when no roles selected */}
                    {availableRolesCount > 0 && selectedRoleIds.length === 0 && (
                      <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 flex items-start gap-3 text-amber-800 dark:text-amber-200">
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-xs leading-relaxed">
                          <span className="font-bold">No role assigned.</span>{' '}
                          Without a role, the user won't be able to perform any actions after approval.
                        </p>
                      </div>
                    )}

                    {/* Selected Role Chips */}
                    {selectedRoles.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedRoles.map((role) => (
                          <span
                            key={role.id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20"
                          >
                            <Check className="w-3 h-3 stroke-[2.5]" />
                            {role.name}
                            <button
                              onClick={() => toggleRoleSelection(role.id)}
                              className="ml-0.5 hover:text-red-500 transition-colors"
                              aria-label={`Remove ${role.name}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Role Search */}
                    {availableRolesCount > 0 && (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          id="role-search-input"
                          type="text"
                          value={roleSearch}
                          onChange={(e) => setRoleSearch(e.target.value)}
                          placeholder="Search roles..."
                          className="w-full pl-9 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm placeholder-slate-400 focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-400 dark:focus:border-emerald-600 outline-none transition-all text-slate-800 dark:text-slate-200"
                        />
                        {roleSearch && (
                          <button
                            onClick={() => setRoleSearch('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )}

                    {/* Role Groups */}
                    <div className="space-y-5 max-h-72 overflow-y-auto pr-1 custom-scrollbar">
                      {filteredGroups.length === 0 && (
                        <div className="py-8 text-center">
                          <UserIcon className="w-8 h-8 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                          <p className="text-xs text-slate-500 dark:text-slate-400">No roles match your search</p>
                        </div>
                      )}

                      {filteredGroups.map((group, groupIdx) => (
                        <div key={groupIdx} className="space-y-2">
                          {/* Group Label */}
                          <div className="flex items-center gap-1.5">
                            {group.logoUrl ? (
                              <img src={group.logoUrl} alt="" className="w-3.5 h-3.5 rounded object-cover" />
                            ) : group.isApp ? (
                              <AppWindow className="w-3.5 h-3.5 text-slate-400" />
                            ) : (
                              <Building className="w-3.5 h-3.5 text-slate-400" />
                            )}
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                              {group.isApp ? `App: ${group.groupLabel}` : group.groupLabel} ({group.roles.length})
                            </span>
                          </div>

                          {/* Role Grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {group.roles.map((role) => {
                              const isSelected = selectedRoleIds.includes(role.id);
                              return (
                                <div
                                  key={role.id}
                                  id={`role-item-${role.id}`}
                                  onClick={() => toggleRoleSelection(role.id)}
                                  className={`p-3 rounded-xl border transition-all cursor-pointer select-none ${isSelected
                                    ? 'bg-emerald-500/8 border-emerald-500/40 dark:border-emerald-500/30 shadow-xs'
                                    : 'bg-slate-50/50 dark:bg-slate-900/50 border-slate-200/60 dark:border-slate-800/60 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40'
                                    }`}
                                >
                                  <div className="flex items-start gap-2.5">
                                    {/* Checkbox */}
                                    <div
                                      className={`w-4.5 h-4.5 mt-0.5 rounded flex items-center justify-center border transition-colors shrink-0 ${isSelected
                                        ? 'bg-emerald-500 border-emerald-500 text-white'
                                        : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900'
                                        }`}
                                      style={{ width: '18px', height: '18px' }}
                                    >
                                      {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                                    </div>
                                    {/* Info */}
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{role.name}</p>
                                      {role.description && (
                                        <p className="text-[10px] text-slate-400 mt-0.5 truncate">{role.description}</p>
                                      )}
                                      <span className={`inline-block mt-1 text-[9px] font-mono font-medium px-1.5 py-0.5 rounded ${group.isApp
                                        ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20'
                                        : 'bg-slate-200/70 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-300/40 dark:border-slate-700/50'
                                        }`}>
                                        {getRoleKindLabel(role)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Justification / Comment ── */}
              <div className={`rounded-2xl border overflow-hidden transition-colors duration-200 ${actionType === 'approve'
                ? 'border-slate-100 dark:border-slate-800'
                : 'border-red-200/60 dark:border-red-900/30'
                }`}>
                <div className={`px-5 py-3.5 border-b flex items-center gap-2 ${actionType === 'approve'
                  ? 'bg-slate-50/50 dark:bg-slate-800/30 border-slate-100 dark:border-slate-800'
                  : 'bg-red-50/50 dark:bg-red-950/20 border-red-200/60 dark:border-red-900/30'
                  }`}>
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${actionType === 'approve' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                    {actionType === 'approve'
                      ? <ShieldCheck className="w-4 h-4" />
                      : <ShieldAlert className="w-4 h-4" />}
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                    Justification for {actionType === 'approve' ? 'Approval' : 'Rejection'}
                  </h4>
                  <span className="text-[10px] text-red-500 font-semibold ml-1">Required</span>
                </div>
                <div className="p-5">
                  <textarea
                    id="justification-textarea"
                    rows={4}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={
                      actionType === 'approve'
                        ? 'Explain why this user should be granted access...'
                        : 'Explain the reason for rejecting this access request...'
                    }
                    className={`w-full p-3.5 bg-slate-50 dark:bg-slate-900 border rounded-xl text-sm placeholder-slate-400 focus:ring-4 outline-none transition-all resize-none text-slate-800 dark:text-slate-200 ${actionType === 'approve'
                      ? 'border-slate-200/60 dark:border-slate-700/60 focus:ring-emerald-500/10 focus:border-emerald-400 dark:focus:border-emerald-600'
                      : 'border-red-200/60 dark:border-red-800/30 focus:ring-red-500/10 focus:border-red-400 dark:focus:border-red-700'
                      }`}
                  />
                  <p className="text-[10px] text-slate-400 mt-2">
                    This justification will be logged and visible to the user and auditors.
                  </p>
                </div>
              </div>

              {/* ── Action Buttons ── */}
              <div className="flex items-center gap-3 pt-1">
                <Button
                  id="confirm-action-btn"
                  onClick={handleReview}
                  disabled={submitting}
                  className={`flex-1 rounded-full py-2.5 flex items-center justify-center gap-2 text-white font-bold cursor-pointer transition-all ${actionType === 'approve'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-red-600 hover:bg-red-700'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin w-4 h-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    <>
                      {actionType === 'approve'
                        ? <ShieldCheck className="w-4 h-4" />
                        : <ShieldAlert className="w-4 h-4" />}
                      {actionType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                    </>
                  )}
                </Button>

                <Button
                  id="cancel-action-btn"
                  onClick={handleCancel}
                  variant="outline"
                  className="rounded-full px-6 py-2.5 font-bold cursor-pointer border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

        </div>
      </div>
    </PageLayout>
  );
};
